const NCB_CONFIG = {
  conversationId: 'chat-f28fc974-301b-404b-96a1-f63da34a7322',
  proxyBase: 'https://rmvzorxcl35mttidiexhtp5g2m0hpsqo.lambda-url.us-east-2.on.aws',
  instance: '51822_floorcraft'
};

const SESSION_TOKEN_KEY = 'ncb_session_token';

function readSessionToken() {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

function buildProxyUrl(path) {
  const url = new URL(path, NCB_CONFIG.proxyBase);
  url.searchParams.set('Instance', NCB_CONFIG.instance);
  return url.toString();
}

async function clearSessionCookie() {
  try {
    await fetch(buildProxyUrl('/auth/sign-out'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(readSessionToken()
          ? { Authorization: `Bearer ${readSessionToken()}` }
          : {})
      },
      body: JSON.stringify({})
    });
  } catch {
    // Stan lokalny zostanie wyczyszczony przez komponent profilu.
  }
}

async function ncbRequest(url, options = {}) {
  try {
    const sessionToken = readSessionToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      ...options.headers
    };

    const response = await fetch(url, {
      credentials: 'include',
      ...options,
      headers
    });

    const body = await response.json().catch(() => ({}));

    if (response.ok && body?.token) {
      try {
        localStorage.setItem(SESSION_TOKEN_KEY, body.token);
      } catch {
        // Cookie pozostaje podstawowym mechanizmem sesji.
      }
    }

    if (!response.ok) {
      return {
        status: 'error',
        data: null,
        error: body?.message || body?.error || `HTTP ${response.status}`,
        httpStatus: response.status
      };
    }

    if (
      body &&
      typeof body === 'object' &&
      !Array.isArray(body) &&
      typeof body.status === 'string' &&
      'data' in body
    ) {
      if (url.includes('/auth/delete-user')) {
        await clearSessionCookie();
        localStorage.removeItem(SESSION_TOKEN_KEY);
      }

      return body;
    }

    const result = {
      status: 'success',
      data: body
    };

    if (url.includes('/auth/delete-user')) {
      await clearSessionCookie();
      localStorage.removeItem(SESSION_TOKEN_KEY);
    }

    return result;
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error?.message || 'Błąd sieci'
    };
  }
}

function parseProfilePayload(options) {
  if (typeof options?.body !== 'string') return null;

  try {
    const payload = JSON.parse(options.body);
    return payload?.user_id ? payload : null;
  } catch {
    return null;
  }
}

async function upsertProfile(path, options, payload) {
  const [, queryString = ''] = path.split('?');
  const query = new URLSearchParams(queryString);
  query.set('user_id', payload.user_id);

  const existingResponse = await ncbRequest(
    buildProxyUrl(`/data/read/profiles?${query.toString()}`)
  );

  if (existingResponse?.status !== 'success') {
    return {
      status: 'error',
      data: null,
      error: existingResponse?.error ?? 'Nie udało się sprawdzić istniejącego profilu.'
    };
  }

  const profiles = Array.isArray(existingResponse?.data)
    ? existingResponse.data
    : [];

  const existingProfile = profiles.find(
    (profile) => profile?.user_id === payload.user_id && profile?.id != null
  );

  if (existingProfile?.id != null) {
    return ncbRequest(
      buildProxyUrl(`/data/update/profiles/${existingProfile.id}?${query.toString()}`),
      {
        ...options,
        method: 'PUT',
        body: JSON.stringify(payload)
      }
    );
  }

  return ncbRequest(
    buildProxyUrl(`/data/create/profiles?${query.toString()}`),
    {
      ...options,
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
}

async function deleteRowsForUser(table, userId) {
  const query = `read/${table}?user_id=${encodeURIComponent(userId)}`;
  const response = await ncbRequest(buildProxyUrl(`/data/${query}`));

  if (response?.status !== 'success') {
    return {
      success: false,
      error: response?.error || `Nie udało się odczytać tabeli ${table}.`
    };
  }

  const records = Array.isArray(response?.data) ? response.data : [];

  for (const record of records) {
    if (record?.id === null || record?.id === undefined) continue;

    const deleteResponse = await ncbRequest(
      buildProxyUrl(
        `/data/delete/${table}/${record.id}?owner_id=${encodeURIComponent(userId)}`
      ),
      { method: 'DELETE' }
    );

    if (deleteResponse?.status !== 'success') {
      return {
        success: false,
        error: deleteResponse?.error || `Nie udało się usunąć rekordu z tabeli ${table}.`
      };
    }
  }

  return { success: true };
}

async function cleanupUserDataBeforeAccountDelete(userId) {
  for (const table of ['projects', 'project_versions', 'profiles', 'subscriptions']) {
    const result = await deleteRowsForUser(table, userId);

    if (!result.success) {
      return result;
    }
  }

  return { success: true };
}

export function authFetch(path, options = {}) {
  if (path === 'delete-user') {
    const userId = localStorage.getItem('user_id');

    if (!userId) {
      return Promise.resolve({
        status: 'error',
        data: null,
        error: 'Brak identyfikatora użytkownika.'
      });
    }

    return cleanupUserDataBeforeAccountDelete(userId).then((cleanupResult) => {
      if (!cleanupResult.success) {
        return {
          status: 'error',
          data: null,
          error: cleanupResult.error
        };
      }

      return ncbRequest(buildProxyUrl(`/auth/${path}`), options);
    });
  }

  return ncbRequest(buildProxyUrl(`/auth/${path}`), options);
}

export function dataFetch(path, options = {}) {
  const isProfileSave = /^((create)|(update))\/profiles(?:\/\d+)?(?:\?|$)/.test(path);
  const payload = isProfileSave ? parseProfilePayload(options) : null;

  if (payload) {
    return upsertProfile(path, options, payload);
  }

  return ncbRequest(buildProxyUrl(`/data/${path}`), options);
}

export function publicDataFetch(path, options = {}) {
  return ncbRequest(buildProxyUrl(`/public-data/${path}`), options);
}

export async function uploadFile(file) {
  const formData = new FormData();
  formData.append('file', file, file.name || 'upload');

  try {
    const sessionToken = readSessionToken();
    const response = await fetch('/.netlify/functions/upload-file', {
      method: 'POST',
      credentials: 'include',
      headers: sessionToken
        ? { Authorization: `Bearer ${sessionToken}` }
        : {},
      body: formData
    });

    const body = await response.json().catch(() => ({}));

    if (!response.ok || !body?.url) {
      return {
        status: 'error',
        data: null,
        error: body?.error || `HTTP ${response.status}`
      };
    }

    return {
      status: 'success',
      data: { url: body.url }
    };
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error?.message || 'Nie udało się wysłać pliku.'
    };
  }
}

export async function uploadDataUrl(dataUrl, filename = 'asset.jpg') {
  if (!dataUrl?.startsWith?.('data:image/')) return dataUrl || '';

  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const file = new File([blob], filename, {
    type: blob.type || 'image/jpeg'
  });
  const uploadResponse = await uploadFile(file);

  if (uploadResponse?.status !== 'success') {
    throw new Error(uploadResponse?.error || 'Nie udało się wysłać obrazu.');
  }

  return uploadResponse.data.url;
}

export async function billingFetch(path, options = {}) {
  try {
    const response = await fetch(`/.netlify/functions/${path}`, {
      credentials: 'include',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    const body = await response.json().catch(() => ({}));

    if (!response.ok) {
      return {
        status: 'error',
        data: null,
        error: body?.error || body?.message || `HTTP ${response.status}`,
        httpStatus: response.status
      };
    }

    return {
      status: 'success',
      data: body
    };
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error?.message || 'Błąd sieci'
    };
  }
}

export {
  NCB_CONFIG,
  buildProxyUrl
};